import { datadogLogs } from "@datadog/browser-logs";

import type { Agent, Organization } from "@superblocksteam/shared";

import {
  getDpUrlForProfile,
  getRoutingConfig,
} from "core/utils/dp-routing-config";
import {
  SUPERBLOCKS_UI_AGENT_BASE_URL,
  SUPERBLOCKS_UI_DP_CONFIG_URL,
} from "env";
import { callHelper } from "store/utils/client";
import type { HttpCallOptions } from "store/utils/client";
import { noActiveAgentMessage } from "utils/error/error";

import { orgIsOnPremise } from "../organizations/utils";

type ParametersExceptFirst<F> = F extends (arg0: any, ...rest: infer R) => any
  ? R
  : never;

export type CallOrchestratorPayload = HttpCallOptions & {
  agents: Agent[];
  organization: Organization;
  enableDataPlaneGateway?: boolean;
  profileKey?: string;
};

// baseUrl gets ignored if on premise and agents are supplied, unless enableDataPlaneGateway is true
export async function callOrchestrator<T>(
  payload: CallOrchestratorPayload,
  ...restOfArgs: ParametersExceptFirst<typeof callHelper>
): Promise<
  | T
  | void
  | {
      systemError: string;
    }
> {
  const { agents, organization, enableDataPlaneGateway, profileKey, ...rest } =
    payload;
  const isOnPremise = orgIsOnPremise(organization);

  if (isOnPremise && !enableDataPlaneGateway && !agents.length) {
    console.warn(
      `No active agent of type ${organization.agentType} found for organization ${organization.id}`,
    );
    return {
      systemError: noActiveAgentMessage(organization.agentType),
    };
  }

  let finalBaseUrl: string | undefined;
  if (enableDataPlaneGateway) {
    const routingConfig = await getRoutingConfig(SUPERBLOCKS_UI_DP_CONFIG_URL);
    const dpUrl = getDpUrlForProfile(routingConfig, profileKey);
    if (dpUrl) {
      finalBaseUrl = dpUrl.endsWith("/") ? dpUrl : `${dpUrl}/`;
    } else {
      datadogLogs.logger.warn(
        "[dp-routing] No DP URL found for profile; falling back to control plane",
        { profileKey },
      );
      finalBaseUrl = new URL(
        SUPERBLOCKS_UI_AGENT_BASE_URL,
        window.location.origin,
      ).toString();
    }
  } else if (isOnPremise) {
    finalBaseUrl = agents[0].url;
  } else {
    finalBaseUrl = rest.baseUrl;
  }

  const result = await callHelper<T>(
    {
      ...rest,
      baseUrl: finalBaseUrl,
    },
    ...restOfArgs,
  );

  return result;
}
